import { FontType } from "../../../../common/TextUtils"
import { Buttons } from "../../../generic-sprites/Buttons"
import { Rectangle } from "../../../generic-sprites/Shape"
import { Audio } from "../../../utils/Audio"
import { CameraZoom } from "../../../utils/CameraZoom"
import { PhaserText } from "../../../utils/PhaserText"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { PlaygroundPromise, PlaygroundRectangleColor, PlaygroundRGBHex, PlaygroundRoundText } from "../PlaygroundType"
import { KidPromise } from "./KidPromise"

export class PlaygroundDisplay {
    static textDisplay = (game: Phaser.Game, group: Phaser.Group, texts: Array<PlaygroundRoundText>): Array<Phaser.Text> => {
        const spacing = Scale.y(0.03)
        let y = Scale.y(0.3)
        return texts.map((text: PlaygroundRoundText) => {
            const current = PhaserText.create(game, text.text, {
                x: Scale.x(0.06),
                y,
                group,
                fontPx: 14,
                wordWrapWidth: 0.38,
                type: (text.style) ? text.style : FontType.BODY
            })
            if (text.audio) {
                Buttons.makeSpriteButton(current, () => {
                    Audio.add(game, text.audio)
                })

                y += current.height + spacing
            } else {
                current.y = y
                y += current.height + Scale.y(0.05)
            }
            return current
        })
    }

    static colorDisplay = (game: Phaser.Game, group: Phaser.Group, colors: Array<PlaygroundRGBHex>): Array<PlaygroundRectangleColor> => {
        const x1 = Scale.x(0.05)
        const x2 = Scale.x(0.25)
        let y = Scale.y(0.3)
        return colors.map((color: PlaygroundRGBHex, index: number) => {
            const check = index % 2 === 1
            const current = new Rectangle(
                game,
                check ? x2 : x1,
                y,
                Scale.x(0.1),
                Scale.y(0.1),
                {group, lineColor: color.rgb, fillColor: color.rgb})

            if (check) {
                y += Scale.y(0.15)
            }

            return {rectangle: current, color: color.hex}
        })
    }

    static imageDisplay = (game: Phaser.Game, group: Phaser.Group, images: Array<string>): Array<Phaser.Sprite> => {
        const x1 = Scale.x(0.05)
        const x2 = Scale.x(0.17)
        const x3 = Scale.x(0.29)
        const x4 = Scale.x(0.41)
        let y = Scale.y(0.3)
        return images.map((sprite: string, index: number) => {
            let col: number
            switch (index % 4) {
                case 0:
                    col = x1
                    break
                case 1:
                    col = x2
                    break
                case 2:
                    col = x3
                    break
                case 3:
                    col = x4
                    break
                default:
                    col = x1
                    break
            }

            const current = Sprites.add(game, sprite, {
                x: col,
                y,
                group
            })

            current.pivot.set(0.5, 0.5)

            if (index % 4 === 3) {
                y += Scale.y(0.12)
            }
            return current
        })
    }

    static reviewDisplay = (
        game: Phaser.Game,
        group: Phaser.Group,
        displayPromise: KidPromise,
        rectGroup: Phaser.Group,
        promises: Array<PlaygroundPromise>
    ) => {
        const x1 = Scale.x(0.05)
        const x2 = Scale.x(0.20)
        const x3 = Scale.x(0.40)
        const x4 = Scale.x(0.60)
        const x5 = Scale.x(0.80)
        let y = Scale.y(0.05)
        const fontSize = 10

        const displayButton = Buttons.create(game, "Close", () => {
                group.forEach((child) => child.visible = true)
                rectGroup.forEach((child) => child.inputEnabled = true)
                displayPromise.destroy()
                displayButton.setInvisible()
            },
            {
                x: Scale.x(0.4),
                y: Scale.y(0.85)
            })

        displayButton.setInvisible()

        promises.forEach((promise: PlaygroundPromise, index: number) => {
            let col: number
            switch (index % 5) {
                case 0:
                    col = x1
                    break
                case 1:
                    col = x2
                    break
                case 2:
                    col = x3
                    break
                case 3:
                    col = x4
                    break
                case 4:
                    col = x5
                    break
                default:
                    col = x1
                    break
            }
            const currentPromise = KidPromise.createPromiseText(game, group, promise.promise, promise.color, promise.style, col, y, fontSize, 0.15)
            const usernameY = KidPromise.usernameY(currentPromise.y, currentPromise.height)
            const currentUsername = KidPromise.createPromiseText(
                game, group, "- " + promise.screenname, promise.color, promise.style, col, usernameY, fontSize, 0.15
            )
            const stickerX = KidPromise.stickerX(currentUsername.x, currentUsername.width)
            const sprite = KidPromise.createStickerSprite(game, group, promise.sticker, stickerX, usernameY, 0.5)
            const rectangle = new Rectangle(game, col, y, currentPromise.width, currentPromise.height + sprite.height + Scale.y(0.02), {group: rectGroup})

            rectangle.graphics.visible = false
            rectangle.invisibleSprite.inputEnabled = true
            rectangle.invisibleSprite.input.useHandCursor = true

            rectangle.invisibleSprite.events.onInputDown.add(() => {
                group.forEach((child) => child.visible = false)
                rectGroup.forEach((child) => child.inputEnabled = false)

                displayPromise.selectedText = promise.promise
                displayPromise.selectedStyle = promise.style
                displayPromise.selectedColor = promise.color
                displayPromise.selectedSprite = promise.sticker
                displayPromise.selectedUsername = promise.screenname
                displayPromise.centerPhrase()

                displayButton.setVisible()
            })


            if (index % 5 === 4) {
                y += Scale.y(0.2)
            }
        })
    }

    private static zoomIn = (game: Phaser.Game, graphic: Phaser.Graphics, sprite: Phaser.Sprite) => {
        CameraZoom.zoom(game, graphic)
        sprite.events.onInputDown.addOnce(() => PlaygroundDisplay.zoomOut(game, graphic, sprite))
    }

    private static zoomOut = (game: Phaser.Game, graphic: Phaser.Graphics, sprite: Phaser.Sprite) => {
        CameraZoom.resetZoom(game)
        sprite.events.onInputDown.add(() => PlaygroundDisplay.zoomIn(game, graphic, sprite))
    }
}
